import { Suspense, lazy } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/components/providers/AuthProvider";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { Spinner } from "@/components/ui/spinner";
import type { PageKey } from "@hawk/shared";
import { Redirect } from "wouter";
import {
  isConnectedRoutePath,
  LEGACY_ROUTE_REDIRECTS,
} from "@/lib/navigation-routes";
import { ContactButton } from "@/components/shared/ContactButton";
import LoginPage from "@/pages/auth/login";

const NotFound = lazy(() => import("@/pages/not-found"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const VisitsPage = lazy(() => import("@/pages/visits"));
const BadgesPage = lazy(() => import("@/pages/badges"));
const VisitorBadgePoolPage = lazy(() => import("@/pages/visitor-badge-pool"));
const SpecialBadgePoolPage = lazy(() => import("@/pages/special-badge-pool"));
const ContractorBadgePoolPage = lazy(() => import("@/pages/contractor-badge-pool"));
const BadgePrintQueuePage = lazy(() => import("@/pages/badge-print-queue"));
const BadgeRequestsPage = lazy(() => import("@/pages/badge-requests"));
const InitialBadgeEnrollmentPage = lazy(() => import("@/pages/initial-badge-enrollment"));
const PeoplePage = lazy(() => import("@/pages/people"));
const SettingsPage = lazy(() => import("@/pages/settings"));
const CatalogPage = lazy(() => import("@/pages/settings/catalog"));
const DevicesSettingsPage = lazy(() => import("@/pages/settings/devices"));
const BadgeTemplatesSettingsPage = lazy(() => import("@/pages/settings/badge-templates"));
const AdministrationPage = lazy(() => import("@/pages/settings/administration"));
const ReportsPage = lazy(() => import("@/pages/reports"));
const IntelligencePage = lazy(() => import("@/pages/intelligence"));
const DeploymentPage = lazy(() => import("@/pages/deployment"));
const PlatformPage = lazy(() => import("@/pages/platform"));
const ActivateAccountPage = lazy(() => import("@/pages/auth/activate-account"));
const HawkEyePage = lazy(() => import("@/pages/hawk-eye"));
const AccessProfilesPage = lazy(() => import("@/pages/access-profiles"));
const PersistentMainLayout = lazy(() =>
  import("@/components/layout/MainLayout").then((module) => ({
    default: module.MainLayout,
  })),
);

// Wrapper component to protect routes that require authentication
function Protected({
  children,
  pageKey,
}: {
  children: React.ReactNode;
  pageKey?: PageKey;
}) {
  return <ProtectedRoute pageKey={pageKey}>{children}</ProtectedRoute>;
}

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
      <div className="inline-flex items-center gap-2 text-sm">
        <Spinner />
        Loading HAWK…
      </div>
    </div>
  );
}

function ConnectedRouteFallback() {
  return (
    <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center text-muted-foreground">
      <div className="inline-flex items-center gap-2 text-sm">
        <Spinner />
        Chargement de la page…
      </div>
    </div>
  );
}

function ConnectedRouter() {
  return (
    <Protected>
      <PersistentMainLayout>
        <Suspense fallback={<ConnectedRouteFallback />}>
          <Switch>
        <Route path="/dashboard">
          <Protected pageKey="dashboard"><Dashboard /></Protected>
        </Route>
        <Route path="/visits">
          <Protected pageKey="visits"><VisitsPage /></Protected>
        </Route>
        <Route path="/badges/requests">
          <Protected pageKey="badgeRequests"><BadgeRequestsPage /></Protected>
        </Route>
        <Route path="/badges/initial-enrollment">
          <Protected pageKey="badgeInitialEnrollment"><InitialBadgeEnrollmentPage /></Protected>
        </Route>
        <Route path="/badges/production/requests">
          <Protected pageKey="badgeRequests"><BadgeRequestsPage productionWorkspace /></Protected>
        </Route>
        <Route path="/badges/production/direct">
          <Protected pageKey="badgeInitialEnrollment"><InitialBadgeEnrollmentPage productionWorkspace /></Protected>
        </Route>
        <Route path="/badges/production/finalize">
          <Protected pageKey="badgeIssuance"><BadgesPage initialFilter="pending" productionLane="finalize" /></Protected>
        </Route>
        <Route path="/badges/production/ready">
          <Protected pageKey="badgeIssuance"><BadgesPage initialFilter="pending" productionLane="ready" /></Protected>
        </Route>
        <Route path="/badges/templates">
          <Protected pageKey="settingsBadgeTemplates"><BadgeTemplatesSettingsPage /></Protected>
        </Route>
        <Route path="/badges/visitor-pool">
          <Protected pageKey="badgeIssuance"><VisitorBadgePoolPage /></Protected>
        </Route>
        <Route path="/badges/special-pool">
          <Protected pageKey="badgeIssuance"><SpecialBadgePoolPage /></Protected>
        </Route>
        <Route path="/badges/contractor-pool">
          <Protected pageKey="badgeIssuance"><ContractorBadgePoolPage /></Protected>
        </Route>
        <Route path="/badges/print-queue">
          <Protected pageKey="badgeIssuance"><BadgePrintQueuePage /></Protected>
        </Route>
        <Route path="/badges/all">
          <Protected pageKey="badges"><BadgesPage initialFilter="all" /></Protected>
        </Route>
        <Route path="/badges/permanent">
          <Protected pageKey="badges"><BadgesPage initialFilter="permanent" /></Protected>
        </Route>
        <Route path="/badges/visitor">
          <Protected pageKey="badges"><BadgesPage initialFilter="visitor" /></Protected>
        </Route>
        <Route path="/badges/special">
          <Protected pageKey="badges"><BadgesPage initialFilter="special" /></Protected>
        </Route>
        <Route path="/badges/contractor">
          <Protected pageKey="badges"><BadgesPage initialFilter="contractor" /></Protected>
        </Route>
        <Route path="/directory/people">
          <Protected pageKey="people"><PeoplePage /></Protected>
        </Route>
        <Route path="/directory/users">
          <Protected pageKey="settingsAdministration"><AdministrationPage section="users" /></Protected>
        </Route>

        <Route path="/access-control/zones">
          <Protected pageKey="settingsCatalog"><CatalogPage section="zones" /></Protected>
        </Route>
        <Route path="/access-control/profiles">
          <Protected pageKey="accessProfiles"><AccessProfilesPage /></Protected>
        </Route>
        <Route path="/access-control/checkpoints">
          <Protected pageKey="settingsCatalog"><CatalogPage section="checkpoints" /></Protected>
        </Route>
        <Route path="/access-control/terminals">
          <Protected pageKey="devices"><DevicesSettingsPage /></Protected>
        </Route>

        <Route path="/administration/organizations">
          <Protected pageKey="settingsOrganizations"><CatalogPage section="organizations" /></Protected>
        </Route>
        <Route path="/administration/roles">
          <Protected pageKey="settingsAdministration"><AdministrationPage section="roles" /></Protected>
        </Route>
        <Route path="/reports">
          <Protected pageKey="reports"><ReportsPage /></Protected>
        </Route>
        <Route path="/hawk-eye">
          <Protected pageKey="hawkEye"><HawkEyePage /></Protected>
        </Route>
        <Route path="/settings">
          <Protected pageKey="settings"><SettingsPage /></Protected>
        </Route>

        {LEGACY_ROUTE_REDIRECTS.map((redirect) => (
          <Route key={redirect.from} path={redirect.from}>
            <Protected pageKey={redirect.pageKey}>
              <Redirect to={redirect.to} />
            </Protected>
          </Route>
        ))}

        <Route component={NotFound} />
          </Switch>
        </Suspense>
      </PersistentMainLayout>
    </Protected>
  );
}

function RouteResolver() {
  const [location] = useLocation();

  return isConnectedRoutePath(location) ? <ConnectedRouter /> : <NotFound />;
}

function Router() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/">
          <Redirect to="/login" />
        </Route>
        <Route path="/platform" component={PlatformPage} />
        <Route path="/intelligence" component={IntelligencePage} />
        <Route path="/deployment" component={DeploymentPage} />
        <Route path="/login">
          <LoginPage />
        </Route>
        <Route path="/connexion">
          <Redirect to="/login" />
        </Route>
        <Route path="/activate-account" component={ActivateAccountPage} />
        <Route>
          <RouteResolver />
        </Route>
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Router />
          <ContactButton />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
